import React, { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { useForm, FieldValues } from 'react-hook-form'
import { useNavigate } from 'react-router'
import { Alert, Button, Logo, useUserService } from '@climate/components'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { useCreditRatingsService, useEntitiesService } from '../../services'
import MemberAgreementModal from '../../components/MemberAgreementModal'

import AddressSection from './AddressSection'
import OrganizationDetailsSection from './OrganizationDetailsSection'
import { toEntityPayload } from './toEntityPayload'

import LogoImage from '../../../assets/images/logo.png'

export default function OnboardPage() {
  const navigate = useNavigate()
  const { register, control, handleSubmit, getValues, reset, watch, formState: { errors, isDirty } } = useForm()

  const { user, sync, updateOnboardingProfile } = useUserService(window.config.iam_service_url, window.config.session_cookie, 'iam')
  const { entity, updateEntity, getMemberAgreement, acceptAgreement } = useEntitiesService(user.entity_id as string)
  const [agreementOpen, setAgreementOpen] = useState(false)
  const [agreementHtml, setAgreementHtml] = useState<string | undefined>()

  // Buying/selling direction + title are now chosen on this page. Prefill the
  // direction from the user's existing ppa/carbon roles and the title from the
  // session, so a returning draft sees their prior choices.
  const currentDirection = (user.active_roles || [])
    .map((role) => role.split(':'))
    .find(([app]) => app === 'ppa' || app === 'carbon')?.[1]
  const desiredRoleDefault = currentDirection === 'seller' ? 'Seller' : 'Buyer'

  const { creditRatingsAgencies, creditRatingsAgenciesGrouped } = useCreditRatingsService()

  if (user.entity_status === 'active') navigate('/', { replace: true })

  useEffect(() => {
    const run = async () => {
      await sync()
      if (user.entity_status === 'active') navigate('/', { replace: true })
    }
    run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (entity) {
      reset({
        agency_credit_rating_id: entity.agency_credit_rating_id,
        agency_credit_rating_date: entity.agency_credit_rating_date,
        form_of_organization: entity.form_of_organization,
        jurisdiction_country: entity.jurisdiction_country,
        jurisdiction_of_organization: entity.jurisdiction_of_organization,
        jurisdiction_of_organization_identifier: entity.jurisdiction_of_organization_identifier,
        size_type: entity.size_type,
        size_dollars: entity.size_dollars,
        is_utility_company: entity.is_utility_company ? 'Yes' : 'No',
        is_energy_trader: entity.is_energy_trader ? 'Yes' : 'No',
        industry_classification_code: entity.industry_classification_code ?? '',
        user_role: desiredRoleDefault,
        user_title: user.title ?? '',
        ...(entity.notices_address && {
          notices_line1: entity.notices_address.line1,
          notices_city: entity.notices_address.city,
          notices_country: entity.notices_address.country,
          notices_region: entity.notices_address.region,
          notices_postal_code: entity.notices_address.postal_code,
        }),
        ...(entity.billing_address && {
          billing_line1: entity.billing_address.line1,
          billing_city: entity.billing_address.city,
          billing_country: entity.billing_address.country,
          billing_region: entity.billing_address.region,
          billing_postal_code: entity.billing_address.postal_code,
        }),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity])

  const saveAction = async () => {
    const values = getValues()
    await updateEntity(user.entity_id as string, toEntityPayload(values))
    await updateOnboardingProfile({ user_role: values.user_role, user_title: values.user_title })
    toast.success(`Updated ${entity!.name} information`)
  }

  const openAgreement = async () => {
    setAgreementOpen(true)
    try {
      const { html } = await getMemberAgreement(entity!.id)
      setAgreementHtml(html)
    } catch {
      setAgreementOpen(false)
      toast.error('We could not load the Member Agreement. Please try again.')
    }
  }

  const agreeAction = async () => {
    try {
      await acceptAgreement(entity!.id)
    } catch {
      toast.error('We could not record your agreement. Please try again.')
      return
    }
    setAgreementOpen(false)
    toast.success('Thank you. Your application has been submitted for review')
    location.reload()
  }

  const onboardAction = async (data: FieldValues) => {
    await updateEntity(user.entity_id as string, toEntityPayload(data))
    await updateOnboardingProfile({ user_role: data.user_role, user_title: data.user_title })
    await openAgreement()
  }

  const noticesCountry = watch('notices_country', '')
  const billingCountry = watch('billing_country', '')
  const jurisdictionCountry = watch('jurisdiction_country', 'United States')

  const entityCreditRating = useMemo(
    () => creditRatingsAgencies?.find(({ id }) => id === entity?.agency_credit_rating_id),
    [creditRatingsAgencies, entity?.agency_credit_rating_id]
  )

  const isDraft = entity?.status === 'draft'

  return (
    <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <MemberAgreementModal
        isOpen={agreementOpen}
        setIsOpen={setAgreementOpen}
        html={agreementHtml}
        onAgree={agreeAction}
      />
      <div className="sm:mx-auto sm:w-full sm:max-w-2xl">
        <Logo LogoImage={LogoImage} alt="Tellus Iam logo" />
        {entity && Object.keys(creditRatingsAgenciesGrouped).length && (
          <form onSubmit={handleSubmit(onboardAction)}>
            <h2 className="mt-6 text-center text-2xl font-bold text-gray-900">
              {"Please continue your Organization's Onboarding"}
            </h2>
            {entity?.status === 'review' && (
              <div className="mt-6">
                <Alert id="onboarding_complete_alert" type="success"
                  message="Onboarding completed! Look out for an email with the status of application review" />
              </div>
            )}

            <div className="mt-8">
              <div className="bg-white py-8 px-4 shadow-sm sm:rounded-lg sm:px-10 space-y-4">
                <AddressSection
                  title="Organization address for notices"
                  prefix="notices"
                  isDraft={isDraft}
                  address={entity.notices_address}
                  countryValue={noticesCountry}
                  register={register}
                  errors={errors}
                />

                <AddressSection
                  title="Organization billing address"
                  prefix="billing"
                  isDraft={isDraft}
                  address={entity.billing_address}
                  countryValue={billingCountry}
                  register={register}
                  errors={errors}
                />

                <OrganizationDetailsSection
                  isDraft={isDraft}
                  entity={entity}
                  jurisdictionCountry={jurisdictionCountry}
                  creditRatingsAgenciesGrouped={creditRatingsAgenciesGrouped}
                  entityCreditRating={entityCreditRating}
                  register={register}
                  control={control}
                  errors={errors}
                  userTitle={user.title}
                  userRoleLabel={desiredRoleDefault === 'Seller' ? 'Selling' : 'Buying'}
                />
              </div>

              {isDraft && (
                <div className="float-right text-center space-y-4 my-4">
                  <Button
                    id="save_progress"
                    secondary
                    className="mx-4"
                    onClick={() => saveAction()}
                    disabled={!isDirty}
                  >
                    Save progress
                  </Button>
                  <Button id="submit" type="submit">Submit for onboarding</Button>
                </div>
              )}

              {entity.status === 'pending_agreement' && (
                <div className="mt-4 flex flex-col gap-4 rounded-lg bg-white px-6 py-5 shadow-sm sm:flex-row sm:items-center">
                  <InformationCircleIcon className="hidden h-6 w-6 flex-none text-perfect-shine sm:block" aria-hidden="true" />
                  <p className="min-w-0 text-sm text-gray-600 sm:flex-1">
                    To participate in Tellus Markets, please signify your agreement to our Member Agreement by
                    clicking below.
                  </p>
                  <div className="flex-none sm:ml-4">
                    <Button id="review_member_agreement" onClick={() => openAgreement()}>
                      Review Member Agreement
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
